import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useTranslation } from 'react-i18next'
import { supabase } from 'Services/supabaseClient'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { Checkbox } from 'primereact/checkbox'
import { ROUTE_STRINGS } from 'Utils/Constants'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import Loader from 'Components/Loader'
import LayoutContainer from 'Components/LayoutComponent'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'

const AddInternalCommunities = ({ setLoading }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const subscriptionCodeMap = {
    REG: 'R',
    GLD: 'G',
    PLT: 'P'
  }

  const [showErrorDialog, setShowErrorDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onConfirmation: () => {} })
  const [addFormFields, setAddFormFields] = useState({
    communityName: '',
    description: '',
    communityUrl: '',
    subscription: []
  })

  const [inputValidity, setInputValidity] = useState({
    communityName: false,
    subscription: false
  })
  const [subscriptionList, setSubscriptionList] = useState([])

  useEffect(() => {
    getSubscriptionList()
  }, [])

  const getSubscriptionList = async () => {
    setLoading(true)
    try {
      const response = await apiAdapterCoffeeWeb.getAllSubscriptionTypes()
      const allSubscriptionData = response?.data?.returnLst || []
      const activeSubscriptions = allSubscriptionData?.filter((item) => item.isActive)

      setSubscriptionList(activeSubscriptions)
    } catch (error) {
      console.log('Error fetching subscriptions:', error)
    } finally {
      setLoading(false)
    }
  }

  const postInternalCommunities = async () => {
    setShowErrorDialog({ ...showErrorDialog, showModal: false })
    setLoading(true)

    try {
      const { communityName, communityUrl, description, subscription } = addFormFields
      const mappedSubscriptions = subscription?.map((code) => subscriptionCodeMap[code])

      const postObject = {
        name: communityName,
        community_url: communityUrl,
        description: description,
        required_subscription: mappedSubscriptions,
        is_internal: true
      }

      const { data, error, status } = await supabase.from('communities').insert(postObject).select('*').single()

      if (error) {
        setShowErrorDialog({
          ...showErrorDialog,
          showModal: true,
          modalType: 'error',
          message: t('FAILED_TO_ADD_COMMUNITY'),
          onRetry: () => postInternalCommunities(),
          onHide: handleDismiss
        })
      }

      if (status === 201 && data?.id) {
        setShowErrorDialog({
          ...showErrorDialog,
          showModal: true,
          modalType: 'success',
          message: t('COMMUNITY_ADDED_SUCCESSFULLY'),
          onHide: handleDismiss,
          onSuccess: handleSuccess
        })
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = () => {
    const isCommunityNameValid = !!addFormFields.communityName
    const isSubscriptionValid = addFormFields.subscription && addFormFields.subscription.length > 0

    setInputValidity({
      communityName: true,
      subscription: true
    })

    if (isCommunityNameValid && isSubscriptionValid) {
      postInternalCommunities()
    }
  }

  const handleInputChange = (field, value) => {
    setAddFormFields((prevState) => ({
      ...prevState,
      [field]: value
    }))
  }

  const handleCheckboxChange = (value) => {
    setAddFormFields((prev) => {
      const currentSubs = prev.subscription || []
      const index = subscriptionList.findIndex((item) => item.subscriptionCode === value)

      let updatedValues = [...currentSubs]

      if (updatedValues.includes(value)) {
        updatedValues = updatedValues.filter((code) => subscriptionList.findIndex((item) => item.subscriptionCode === code) < index)
      } else {
        updatedValues = [...new Set([...updatedValues, ...subscriptionList.slice(index).map((s) => s.subscriptionCode)])]
      }

      setInputValidity((prev) => ({ ...prev, subscription: true }))

      return { ...prev, subscription: updatedValues }
    })
  }

  const handleBlur = (fieldName) => {
    setInputValidity((prev) => ({
      ...prev,
      [fieldName]: true
    }))
  }

  const handleDismiss = () => {
    setShowErrorDialog({ ...showErrorDialog, showModal: false })
  }

  const handleSuccess = () => {
    history.push(ROUTE_STRINGS.internalCommunitiesManagement)
  }

  return (
    <LayoutContainer title={t('ADD_INTERNAL_COMMUNITIES')} breadCrumbsTitle={'addInternalCommunities'}>
      <div className="add-internal-communities-wrapper">
        <div className="form-fields-row">
          <div className="form-field">
            <span className="p-float-label">
              <InputText id="communityName" name="communityName" value={addFormFields.communityName} className={`input-control ${inputValidity.communityName && !addFormFields.communityName ? 'error' : ''}`} onChange={(event) => handleInputChange('communityName', event.target.value)} onBlur={() => handleBlur('communityName')} />
              <label htmlFor="communityName" className={`input-label ${inputValidity.communityName && !addFormFields.communityName ? 'error' : ''}`}>
                {t('COMMUNITY_NAME')} <span className="required">*</span>
              </label>
            </span>
            {inputValidity.communityName && !addFormFields.communityName && <small className="error-message">{t('COMMUNITY_NAME_ERROR_MESSAGE')}</small>}
          </div>

          <div className="form-field">
            <span className="p-float-label">
              <InputText id="communityUrl" name="communityUrl" value={addFormFields.communityUrl} className={'input-control'} onChange={(event) => handleInputChange('communityUrl', event.target.value)} />
              <label htmlFor="communityUrl">{t('COMMUNITY_URL')}</label>
            </span>
          </div>
        </div>

        <div className="form-fields-row">
          <div className="form-field">
            <span className="p-float-label">
              <InputTextarea rows={5} id="description" name="description" value={addFormFields.description} className={'input-control'} onChange={(event) => handleInputChange('description', event.target.value)} />
              <label htmlFor="description">{t('COMMUNITY_DESCRIPTION')}</label>
            </span>
          </div>

          <div className="checkbox-wrapper">
            <label className={`section-label ${inputValidity.subscription && (!addFormFields.subscription || addFormFields.subscription.length === 0) ? 'error' : ''}`}>
              {t('SUBSCRIPTION_TYPE')} <span className="required">*</span>
            </label>

            <div className="checkbox-group">
              {subscriptionList?.map((subscription) => (
                <div className="checkbox-option" key={subscription.id}>
                  <Checkbox inputId={subscription.subscriptionCode} name="subscription" value={subscription.subscriptionCode} onChange={(e) => handleCheckboxChange(e.value)} checked={addFormFields.subscription?.includes(subscription.subscriptionCode)} />
                  <label htmlFor={subscription.subscriptionCode}>{subscription.name || subscription.subscriptionCode}</label>
                </div>
              ))}
            </div>

            {inputValidity.subscription && (!addFormFields?.subscription || addFormFields?.subscription?.length === 0) && <small className="error-message">{t('SUBSCRIPTION_TYPE_IS_REQUIRED')}</small>}
          </div>
        </div>

        <div className="community-form-actions">
          <ButtonComponent type="submit" onClick={handleSubmit} disabled={!addFormFields.communityName || !addFormFields.subscription || addFormFields.subscription.length === 0} />
        </div>
      </div>
      <DialogModal {...showErrorDialog} onDismiss={handleDismiss} />
    </LayoutContainer>
  )
}

export default Loader(AddInternalCommunities)
